import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Grid, makeStyles, TextField, Typography } from '@material-ui/core';
import { Circle } from '@mui/icons-material';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { useExercise } from './hooks/useExercise';
import Image from '../common/Image';
import { formateDate, searchArray, sortItemsBySelection } from '../utils/Utils';
import ReactInfiniteScroll from '../common/ReactInfiniteScroll';

const useStyle = makeStyles({
  content: {
    marginTop: '15px',
  },
  searchText: {
    width: '100%',
    background: '#FAFAFA',
    border: '1px solid #E2E2E2',
    padding: ' 10px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
    borderRadius: '8px',
  },
  card: {
    height: '120px',
    background: 'white',
    borderRadius: '8px',
    padding: '10px',
  },
  info: {
    color: '#667085',
    fontSize: '12px ',
  },
  name: {
    color: '#10182B',
  },
  infoBox: {
    width: '65%',
    padding: '4px 6px',
  },
  imageBox: {
    width: '25%',
    height: '100%',
    background: 'grey',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  selectBox: {
    width: '10%',
  },
  editButton: {
    height: '40px',
    width: '100px',
    marginRight: '10px',
    cursor: 'pointer',
  },
  cta: {
    color: '#622FEE',
    fontSize: '14px',
    cursor: 'pointer',
  },
});

export const SuggestedJourney = ({ selectedJourneys, setSelectedJourneys }) => {
  const classes = useStyle();

  const { getFilteredJourney, journeys, plansList, getPlan, getCollections, collectionList } = useExercise();
  const [filteredJ, setFilteredJ] = useState([]);
  const [searchVal, setSearchVal] = useState('');
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);

  useEffect(() => {
    getFilteredJourney();
    getPlan();
    getCollections();
  }, []);

  useEffect(() => {
    setFilteredJ(journeys);
  }, [journeys]);

  const handleSelect = (id) => {
    const temp = [...selectedJourneys];
    const indx = temp.indexOf(id);

    if (indx !== -1) {
      temp.splice(indx, 1);
    } else {
      temp.push(id);
    }
    setSelectedJourneys(temp);
  };

  const handleSearchChange = (e) => {
    const input = e.target.value;
    setSearchVal(input);
    const filteredArray = searchArray(journeys || [], ['title'], input);
    setFilteredJ(filteredArray);
  };

  const handleFilterChange = (name, value) => {
    if (name === 'plans') {
      setSelectedPlans(value);
    }
    if (name === 'collections') {
      setSelectedCollections(value);
    }
  };

  const handleAllPlansToggle = (isCheck) => {
    if (isCheck) {
      setSelectedPlans(plansList);
    } else {
      setSelectedPlans([]);
    }
  };

  const handleAllCollectionsToggle = (isCheck) => {
    if (isCheck) {
      setSelectedCollections(collectionList);
    } else {
      setSelectedCollections([]);
    }
  };

  const handleFilter = () => {
    const planIds = selectedPlans?.map((i) => i.id) || [];
    const collIds = selectedCollections?.map((i) => i.id) || [];
    getFilteredJourney(searchVal, planIds, collIds);
  };

  const handleReset = () => {
    setSelectedCollections([]);
    setSelectedPlans([]);
    setSearchVal('');
    getFilteredJourney();
  };

  // Selected list should move up in the list
  const sortedJourneys = sortItemsBySelection(selectedJourneys, filteredJ, 'journeyId');

  // Render function for each journey item
  const renderJourneyItem = (item) => (
    <Grid item style={{ width: 'calc(33.33% - 20px)', marginBottom: '15px', marginRight: '15px' }} key={item.journeyId}>
      <Grid container className={classes.card}>
        <Grid item className={classes.selectBox}>
          <Checkbox
            color="primary"
            onClick={() => handleSelect(item.journeyId)}
            checked={selectedJourneys?.includes(item.journeyId)}
          />
        </Grid>
        <Grid item className={classes.imageBox}>
          <Image
            alt=""
            src={item.thumbnail || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
        </Grid>
        <Grid item className={classes.infoBox}>
          <Grid container style={{ gap: '5px' }}>
            <Grid item xs={12}>
              <Typography className={classes.name} noWrap>
                {item.title}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container alignItems="center" style={{ gap: '10px' }}>
                <Typography className={classes.info}>{formateDate(item.publishDate)}</Typography>
                <Circle className={classes.info} style={{ fontSize: '10px' }} />
                <Typography className={classes.info}>ID: {item.journeyId}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Grid container style={{ height: '100%' }}>
      <Grid item xs={12}>
        <Grid container style={{ gap: '10px' }}>
          <Grid item xs={12}>
            <TextField
              id="searchId"
              name="search"
              required
              autoComplete="off"
              autoSave="off"
              placeholder="Search journey"
              value={searchVal}
              onChange={handleSearchChange}
              InputProps={{
                disableUnderline: true,
                className: classes.searchText,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Grid container alignItems="center" style={{ gap: '12px' }}>
                  <Grid item>
                    <Grid container>
                      <MultiSelectDropdown
                        selectedData={selectedCollections || []}
                        data={collectionList || []}
                        onChangeFilter={handleFilterChange}
                        onSelectedAllToggle={handleAllCollectionsToggle}
                        name="collections"
                        filedTitle="Collections"
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid container>
                      <MultiSelectDropdown
                        selectedData={selectedPlans || []}
                        data={plansList || []}
                        onChangeFilter={handleFilterChange}
                        onSelectedAllToggle={handleAllPlansToggle}
                        name="plans"
                        filedTitle="Plans"
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      focusRipple={false}
                      className={classes.editButton}
                      onClick={handleFilter}
                    >
                      Apply
                    </Button>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.cta} onClick={handleReset}>
                      Reset
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.content} style={{ marginTop: '15px' }}>
                  <ReactInfiniteScroll
                    size={12}
                    data={sortedJourneys || []}
                    renderItem={renderJourneyItem}
                    height="45vh"
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
